<div class="today-page">
  <section class="day-summary">
    <div *ngIf="dayLoading()" class="loading">Loading day view…</div>
    <div *ngIf="dayError()" class="lm-notice lm-notice--danger">
      {{ dayError() }}
      <button type="button" class="lm-btn" (click)="refreshDayError()">Refresh day</button>
    </div>

    <div *ngIf="day() as currentDay" class="day-panel">
      <h2 class="today-date-label lm-sr-only">
        {{ toDisplayDateForDayDate(currentDay.dayDate) | date:'EEEE, d MMMM y' }}
      </h2>

      <app-day-status-card
        *ngIf="dayStatus() as status"
        [status]="status"
        [cycles]="dayStatusCycles()"
        [dayLabel]="(toDisplayDateForDayDate(currentDay.dayDate) | date:'EEE d MMM') || ''"
        [reorderDisabled]="reorderSubmitting()"
        [celebrationCycle]="celebrationCycle()"
        [celebrationSummary]="celebrationSummary()"
        [lastCompletedWorkCycle]="lastCompletedWorkCycle()"
        [lastCompletedLifeCycle]="lastCompletedLifeCycle()"
        (reorderGrouping)="reorderGrouping($event)"
        (scheduleTypeSelect)="onDayStatusGroupSelect($event)"></app-day-status-card>

      <section *ngIf="upNext() as next; else noUpNext" class="lm-next-card up-next-card" aria-label="Up next activity">
        <p class="lm-overline">Up next</p>
        <p class="lm-next-card__title">{{ next.activity.name }}</p>
        <p class="lm-next-card__meta">
          {{ upNextMetaLabel(next) }} ·
          <span class="lm-days" [class.lm-days--quiet]="next.activity.daysSince === 0" aria-hidden="true">{{ next.activity.daysSinceLabel }}</span>
          <span class="lm-sr-only">, {{ getUpNextDaysAriaLabel(next.activity.daysSince) }}</span>
        </p>
        <a
          class="lm-btn lm-btn--primary lm-next-card__action"
          [routerLink]="['/today/allocation', next.activity.activityTypeId]"
          [queryParams]="{ from: next.group.ordinal }"
          [attr.aria-label]="'Open ' + next.activity.name">
          Open
          <svg lucideChevronRight class="lm-icon lm-icon--sm" aria-hidden="true"></svg>
        </a>
      </section>
      <ng-template #noUpNext>
        <p *ngIf="allGroupsDone()" class="lm-next-done up-next-done">All groups done</p>
      </ng-template>

      <div class="today-body">
      <div *ngIf="activityReorderError()" class="lm-notice lm-notice--danger">
        {{ activityReorderError() }}
        <button type="button" class="lm-btn" (click)="refreshAllocationError()">Refresh day</button>
      </div>
      <h2 class="lm-overline habits-overline">{{ habitsHeading() }}</h2>
      <div id="allocations-panel-habits">
        <ul
          *ngIf="openHabitRows() as rows"
          class="habit-list"
          cdkDropList
          cdkDropListOrientation="vertical"
          [cdkDropListDisabled]="activityReorderSubmitting()"
          (cdkDropListDropped)="onHabitDrop($event)">
          <li
            *ngFor="let habit of rows; trackBy: trackHabit"
            class="lm-row"
            cdkDrag
            cdkDragLockAxis="y"
            [cdkDragStartDelay]="listDragStartDelay"
            [cdkDragDisabled]="activityReorderSubmitting()"
            [@habitGoalFoldMotion]="getHabitMotionAnimation()"
            [@.disabled]="habitMotionDurationMs() === 0"
            [attr.data-habit-id]="habit.activityTypeId"
            [attr.aria-label]="getHabitAriaLabel(habit)"
            role="button"
            tabindex="0"
            (click)="navigateToHabit(habit.activityTypeId)"
            (keydown.enter)="navigateToHabit(habit.activityTypeId)"
            (keydown.space)="navigateToHabit(habit.activityTypeId); $event.preventDefault()">
            <div class="lm-row__main">
              <span class="lm-row__title">
                <span>{{ habit.name }}</span>
                <span *ngIf="habit.isCompleted" class="lm-badge lm-badge--done">Goal met</span>
                <span *ngIf="!habit.isCompleted && habit.hasOpenTasks" class="lm-badge">{{ habit.openTaskCount }} open task{{ habit.openTaskCount === 1 ? '' : 's' }}</span>
              </span>
              <span class="lm-row__meta">
                {{ habit.total }} / {{ habit.target !== null ? habit.target : '—' }} ·
                <span class="lm-days" [class.lm-days--quiet]="habit.daysSince === 0" aria-hidden="true">{{ habit.daysSinceLabel }}</span>
              </span>
            </div>
            <button
              type="button"
              class="lm-btn lm-btn--soft-go"
              (click)="$event.stopPropagation(); incrementHabit(habit.activityTypeId)"
              [disabled]="isHabitSubmitting(habit.activityTypeId)">
              {{ isHabitSubmitting(habit.activityTypeId) ? 'Adding…' : '+1' }}
            </button>
          </li>
          <li *ngIf="rows.length === 0 && doneHabitRows().length === 0" class="empty">
            No habit counters yet. Add one in <a routerLink="/activities">Activities</a>.
          </li>
        </ul>

        <button
          *ngIf="doneHabitRows().length > 0"
          type="button"
          class="lm-fold habits-done-fold"
          [attr.aria-expanded]="isDoneHabitsExpanded()"
          [attr.aria-controls]="isDoneHabitsExpanded() ? doneHabitsListId : null"
          (click)="toggleDoneHabitsExpanded()">
          <svg lucideCheck class="lm-icon lm-icon--sm" aria-hidden="true"></svg>
          <span class="lm-fold__count">{{ doneHabitsCount() }} done</span>
          <svg
            *ngIf="isDoneHabitsExpanded(); else doneHabitsCollapsedChevron"
            lucideChevronUp
            class="lm-icon lm-icon--sm"
            aria-hidden="true"></svg>
          <ng-template #doneHabitsCollapsedChevron>
            <svg lucideChevronDown class="lm-icon lm-icon--sm" aria-hidden="true"></svg>
          </ng-template>
        </button>

        <ul
          *ngIf="isDoneHabitsExpanded()"
          [id]="doneHabitsListId"
          class="habit-list habit-list--done">
          <li
            *ngFor="let habit of doneHabitRows(); trackBy: trackHabit"
            class="lm-row lm-row--done"
            [attr.aria-label]="getHabitAriaLabel(habit)"
            role="button"
            tabindex="0"
            (click)="navigateToHabit(habit.activityTypeId)"
            (keydown.enter)="navigateToHabit(habit.activityTypeId)"
            (keydown.space)="navigateToHabit(habit.activityTypeId); $event.preventDefault()">
            <div class="lm-row__main">
              <span class="lm-row__title">
                <span>{{ habit.name }}</span>
                <span class="lm-badge lm-badge--done">Goal met</span>
              </span>
              <span class="lm-row__meta">
                {{ habit.total }} / {{ habit.target !== null ? habit.target : '—' }} ·
                <span class="lm-days" [class.lm-days--quiet]="habit.daysSince === 0" aria-hidden="true">{{ habit.daysSinceLabel }}</span>
              </span>
            </div>
          </li>
        </ul>
      </div>
      </div>
    </div>
  </section>
</div>
